import { Space } from 'antd';
import { DeleteOutlined, FileAddOutlined } from '@ant-design/icons';
import Table, { ColumnsType } from 'antd/lib/table';
import { isEmpty } from 'lodash';
import { handleCurrency, handleNullValues } from 'components/ui/base/table/TableUtils';
import { FC, ReactNode } from 'react';
import { CdsGoodsItemEntry } from 'store/declarations/uk/cds-declaration';
import getItemNumberColumn from 'views/declarations/common/getItemNumberColumn';
import { ListPayload } from '../../../../core/http/response';
import { TableChangeParams } from '../../../../utils/tableHelpers';
import { ProductsErrorCounts } from 'views/declarations/declaration-tabs/declaration-form/tabs/products/getErrorCountsByRow';
import ProductErrorCount from 'views/declarations/common/ProductErrorCount';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import { formatMass } from 'core/utils/numbers';
import getProductsTablePagination from 'views/declarations/common/getProductsTablePagination';
import getTableRowSelection from 'views/declarations/common/getTableRowSelection';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import { ColumnType } from 'antd/es/table';
import OverflowList from 'components/new-ui/base/overflow-list/OverflowList';

type PackagingEntry = { type?: string; count?: string | number };

interface Props {
    data: ListPayload<CdsGoodsItemEntry> | undefined | null;
    onDelete?: (id: string) => void;
    onEdit: (id: string) => void;
    onAddToTemplate?: (item: string) => void;
    viewOnly?: boolean;
    errorCounts?: ProductsErrorCounts;
    selectedRowKeys?: React.Key[];
    onChange: (params: TableChangeParams) => void;
    onSelectedRowKeysChange?: (keys: React.Key[]) => void;
    showAppliedTemplates?: boolean;
}

const getCommodityCode = (record: CdsGoodsItemEntry) =>
    record.governmentAgencyGoodsItem?.commodity?.classification?.[0]?.id;

const getDescription = (record: CdsGoodsItemEntry) => record.governmentAgencyGoodsItem?.commodity?.description;

const UkProductsTable: FC<Props> = ({
    data,
    onDelete,
    onEdit,
    onAddToTemplate,
    viewOnly,
    errorCounts,
    selectedRowKeys,
    onChange,
    onSelectedRowKeysChange,
    showAppliedTemplates = false,
    ...rest
}) => {
    const getMass = (record: CdsGoodsItemEntry) => {
        const goodsMeasure = record.governmentAgencyGoodsItem?.commodity?.goodsMeasure;
        return { gross: goodsMeasure?.grossMassMeasure, net: goodsMeasure?.netNetWeightMeasure };
    };

    const getPackagingEntries = (record: CdsGoodsItemEntry): PackagingEntry[] => {
        const packaging = record.governmentAgencyGoodsItem?.packaging ?? [];
        return packaging.map((pkg) => ({ type: pkg.typeCode, count: pkg.quantityQuantity }));
    };

    const appliedTemplatesColumn: ColumnType<CdsGoodsItemEntry> = {
        title: 'Templates Applied',
        key: 'appliedTemplates',
        render: (_, record) => {
            const templateNames = (record.appliedItemFieldTemplates ?? []).map(({ templateName }) => templateName);
            return templateNames.length > 0 ? <OverflowList items={templateNames} /> : '-';
        },
    };

    const columns: ColumnsType<CdsGoodsItemEntry> = [
        getItemNumberColumn((record: CdsGoodsItemEntry) => record.governmentAgencyGoodsItem?.sequenceNumeric),
        {
            title: 'Code & Description',
            key: 'COMBINED_NOMENCLATURE_CODE',
            render: (_, record) => {
                const code = getCommodityCode(record);
                const description = getDescription(record);
                return (
                    <div>
                        <div>{code ?? '-'}</div>
                        <div>{description ?? '-'}</div>
                    </div>
                );
            },
            sorter: true,
        },

        ...(showAppliedTemplates ? [appliedTemplatesColumn] : []),

        {
            title: 'Net/Gross Mass',
            dataIndex: 'net_gross_mass',
            key: 'net_gross_mass',
            align: 'right',
            render: (_, record) => {
                const { gross, net } = getMass(record);
                return handleNullValues(
                    <>
                        <div>{formatMass(net) ?? '-'}</div>
                        <div>{formatMass(gross) ?? '-'}</div>
                    </>
                );
            },
        },
        {
            title: 'Packages',
            dataIndex: 'packages',
            key: 'packages',
            render: (_, record) => {
                const entries = getPackagingEntries(record);
                if (!entries.length) return handleNullValues(undefined);
                return handleNullValues(
                    <>
                        {entries.map((entry, i) => (
                            <div key={i}>
                                {entry.count ?? '-'} {entry.type ?? '-'}
                            </div>
                        ))}
                    </>
                );
            },
        },
        {
            title: 'Item Price',
            dataIndex: 'item_price',
            key: 'item_price',
            render: (_, record) => {
                const item = record.governmentAgencyGoodsItem;
                const price = Number(item?.statisticalValueAmount);
                return handleNullValues(handleCurrency(price, item?.statisticalValueAmountCurrencyId));
            },
            sorter: (a, b) =>
                Number(a.governmentAgencyGoodsItem?.statisticalValueAmount ?? 0) -
                Number(b.governmentAgencyGoodsItem?.statisticalValueAmount ?? 0),
        },
        {
            title: 'Tag',
            dataIndex: 'tag',
            key: 'tag',
            render: () => handleNullValues('-'),
            sorter: true,
        },
        {
            width: 1,
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            render: (text, record) => {
                const actions: ReactNode[] = [
                    <IconButton
                        icon={<FileAddOutlined />}
                        tooltipText="Add to templates"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAddToTemplate?.(record.id!);
                        }}
                    />,
                ];

                if (!viewOnly) {
                    actions.push(
                        <IconButton
                            icon={<DeleteOutlined />}
                            tooltipText="Delete"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete?.(record?.id!);
                            }}
                        />
                    );
                }

                return {
                    children: <Space size={8}>{actions}</Space>,
                };
            },
        },
    ];

    const notificationsColumn: ColumnType<any> = {
        dataIndex: 'icon',
        key: 'icon',
        width: 1,
        render: (text, record, index: number) => {
            return errorCounts?.[index] ? <ProductErrorCount count={errorCounts[index]} /> : null;
        },
    };

    const hasErrors = !isEmpty(errorCounts);
    const allColumns: ColumnsType<CdsGoodsItemEntry> = hasErrors ? [notificationsColumn, ...columns] : columns;

    return (
        <StyledTableWrapper>
            <Table
                rowKey="id"
                dataSource={data?.list}
                columns={allColumns}
                rowClassName="row-align-top"
                onRow={(record, rowIndex) => {
                    return {
                        onClick: (event) => {
                            event.stopPropagation();
                            return onEdit && onEdit(record.id!);
                        },
                    };
                }}
                onChange={(pagination, _, sorter) => onChange({ pagination, sorter })}
                pagination={getProductsTablePagination(data)}
                rowSelection={getTableRowSelection<CdsGoodsItemEntry>({
                    viewOnly,
                    selectedRowKeys,
                    onSelectedRowKeysChange,
                    // The list is paginated by the BE, so keep selections made on other pages
                    preserveSelectedRowKeys: true,
                })}
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
                {...rest}
            />
        </StyledTableWrapper>
    );
};
export default UkProductsTable;
